/**
 * Testing & Qualification Card
 *
 * Single compact card for the Study Build "Testing & Qualification"
 * section, matching the visual treatment of the Combined documents
 * card on the same page (see RequirementsDesignCard.tsx). Each row is
 * one line: title, description, status badge, and inline action
 * buttons. The card does NOT expand panels inline — users generate /
 * download via the action button and the in-depth panels live on the
 * Study Documents page.
 *
 * Three row modes are supported:
 *   - generate: standard Firestore-doc-with-version pattern (UAT,
 *     Test Procedure). Subscribes to a Firestore path, surfaces
 *     "Generated · X mins ago" / "Generating…" / "Not yet generated"
 *     plus Generate / Download / Regenerate buttons.
 *   - template: static Word template with no Firestore state (Test
 *     Incident Report). Always shows an "Available" badge plus a
 *     Download button. No Generate.
 *   - comingSoon: placeholder cards (IQ, OQ, PQ). Shows a "Coming
 *     Soon" badge and no actions.
 */

import { GENERATION_LOCK_STALE_MS } from '../../../../functions/src/utils/generationWindow';
import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../firebase';
import { CALLABLE_TIMEOUTS } from '../../constants/firebase';
import { dataManagementDoc } from '../../constants/firestorePaths';
import { dictionaryChangedNotice, type DictionaryChangedNotice } from '../../features/dataManagement/dictionaryChangedNotice';
import { usePermission } from '../permissions/PermissionTooltip';
import RenameNotice from '../../features/renames/RenameNotice';
import { useRenameNoticeOpen } from '../../features/renames/useOpenRenameNotices';
import {
  PrerequisiteNote,
  PrerequisiteState,
  RowPrerequisite,
  TEST_LIST_PREREQUISITE,
} from '../../features/testing/prerequisite';
import { saveAs } from 'file-saver';
import {
  formatRelativeTime,
  tsToDate,
  SectionTheme,
} from '../reqDesign/RequirementsDesignCard';
import {
  FileText,
  Download,
  Play,
  RefreshCw,
  Loader2,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  TestTube2,
  ClipboardCheck,
  ClipboardList,
  ListChecks,
  FileCheck,
  Database,
  Eye,
  X,
  Info,
} from 'lucide-react';
import { displayGenerationError } from '../../utils/generationFailureText';

/* ---------- Configuration ---------- */

const STALE_GENERATION_MS = GENERATION_LOCK_STALE_MS; // the lock window; shared with the backend

interface DocStatusDoc {
  generatedAt?: any;
  /** Written by the generators; the dictionary notice uses the later of the two. */
  lastModifiedAt?: any;
  _generating?: boolean;
  _generationStartedAt?: any;
  _generationError?: string;
  _generationFailedAt?: any;
}

type RowMode = 'generate' | 'template' | 'mockData' | 'comingSoon';

interface BaseRow {
  id: string;
  title: string;
  shortName: string;
  description: string;
  icon: React.ReactNode;
}

interface GenerateRow extends BaseRow {
  mode: 'generate';
  generateCallable: string;
  exportCallable: string;
  /** Path under the firestore root (no leading slash). */
  firestorePath: (projectId: string) => string;
  /**
   * Optional upstream dependency. When present, the row watches the named
   * document and — while the prerequisite is unmet — disables Generate and
   * shows a calm note in place of the buttons instead of letting the user
   * click through to a red error. Rows without one behave exactly as before.
   * See features/testing/prerequisite.ts.
   */
  prerequisite?: RowPrerequisite;
}

interface TemplateRow extends BaseRow {
  mode: 'template';
  exportCallable: string;
}

interface ComingSoonRow extends BaseRow {
  mode: 'comingSoon';
}

interface MockDataRow extends BaseRow {
  mode: 'mockData';
  callable: string;
}

type TestingRow = GenerateRow | TemplateRow | MockDataRow | ComingSoonRow;

const TESTING_THEME: SectionTheme = {
  rowAccent: 'border-l-emerald-300',
  headerBg: 'bg-emerald-50 border-emerald-200',
  headerText: 'text-emerald-700',
  iconBg: 'bg-emerald-100',
  iconText: 'text-emerald-600',
};

const ROWS: TestingRow[] = [
  {
    mode: 'generate',
    id: 'test_plan',
    title: 'Test Plan',
    shortName: 'TP-Plan',
    description:
      'Umbrella plan that scopes test strategy, environment, roles, schedule, entry/exit criteria, and acceptance criteria. References the UAT Protocol, Software Systems Testing Procedure, and Test Incident Report.',
    icon: <FileCheck className="h-4 w-4" />,
    generateCallable: 'generateTestPlanV2',
    exportCallable: 'exportTestPlanDocxV2',
    firestorePath: (pid) => `projects/${pid}/requirements/testPlan`,
  },
  {
    mode: 'generate',
    id: 'uat',
    title: 'UAT Protocol',
    shortName: 'UAT',
    description:
      "Verifies the study build in the EDC matches the protocol: visits, forms, edit checks, randomization, blinding, calculations, workflow, data exports, and audit trail.",
    icon: <TestTube2 className="h-4 w-4" />,
    generateCallable: 'generateUATProtocolV2',
    exportCallable: 'exportUATProtocolDocxV2',
    firestorePath: (pid) => `projects/${pid}/dataManagement/uatProtocol`,
  },
  {
    mode: 'generate',
    id: 'uat_report',
    title: 'UAT Report',
    shortName: 'UAT Rpt',
    description:
      'Study-tailored fillable template for the UAT execution record — pass/fail tabulation, deviation log, outstanding issues, and a multi-functional approval block. Test results, deviations, and signatures are completed during UAT execution.',
    icon: <ClipboardCheck className="h-4 w-4" />,
    generateCallable: 'generateUATReportV2',
    exportCallable: 'exportUATReportDocxV2',
    firestorePath: (pid) => `projects/${pid}/dataManagement/uatReport`,
  },
  {
    mode: 'generate',
    id: 'test_procedure',
    title: 'Software Systems Testing Procedure',
    shortName: 'TP',
    description:
      'Field-level test procedure with one Testing Procedure Form table per CRF form. Test IDs, inputs, and expected outcomes are built deterministically from the data dictionary.',
    icon: <ClipboardCheck className="h-4 w-4" />,
    generateCallable: 'generateTestProcedureV2',
    exportCallable: 'exportTestProcedureDocxV2',
    firestorePath: (pid) => `projects/${pid}/requirements/testProcedure`,
  },
  {
    mode: 'generate',
    id: 'test_list',
    title: 'Test List',
    shortName: 'TL',
    description:
      'Tabular inventory of every Test Procedure case mapped to the System Requirement number(s) it verifies, plus a Test Type classification. Run after the Test Procedure has been generated.',
    icon: <ListChecks className="h-4 w-4" />,
    generateCallable: 'generateTestListV2',
    exportCallable: 'exportTestListDocxV2',
    firestorePath: (pid) => `projects/${pid}/requirements/testList`,
    // The Test List is a pure projection over the Test Procedure's test cases,
    // so there is nothing to build until those cases exist.
    prerequisite: TEST_LIST_PREREQUISITE,
  },
  {
    mode: 'template',
    id: 'test_incident_report',
    title: 'Test Incident Report Template',
    shortName: 'TIR',
    description:
      'Word template for recording incidents observed during execution of the Test Procedure. Pre-populated with study metadata; incident rows are filled in by hand during execution.',
    icon: <ClipboardList className="h-4 w-4" />,
    exportCallable: 'exportTestIncidentReportTemplateDocxV2',
  },
  {
    mode: 'template',
    id: 'test_report',
    title: 'Testing Report Template',
    shortName: 'TR',
    description:
      'Past-tense counterpart of the Test Plan. Word template for documenting test execution outcomes — features tested, features not tested, methodology, and execution summary. Pre-populated with study metadata.',
    icon: <FileText className="h-4 w-4" />,
    exportCallable: 'exportTestReportTemplateDocxV2',
  },
  {
    mode: 'mockData',
    id: 'mock_data',
    title: 'Mock Data Generator',
    shortName: 'Mock',
    description:
      "Realistic synthetic test records (one CSV per CRF form) for UAT, training, and validation. Detects the project's EDC and emits REDCap-style import format; other EDCs include a manual-load notice in the README.",
    icon: <Database className="h-4 w-4" />,
    callable: 'generateMockDataV2',
  },
];

/* ---------- Generate row ---------- */

interface GenerateRowProps {
  row: GenerateRow;
  projectId: string;
  /** dataManagement/dictionaryAdoption.adoptedAt, read once by the card. */
  adoptedAt: unknown;
}

function GenerateRowView({ row, projectId, adoptedAt }: GenerateRowProps) {
  const canGenerate = usePermission('generate');
  const renameNoticeOpen = useRenameNoticeOpen(projectId, row.firestorePath(projectId));
  const [statusDoc, setStatusDoc] = useState<DocStatusDoc | null>(null);
  const [exists, setExists] = useState<boolean>(false);
  type Working = 'generate' | 'download' | null;
  const [working, setWorking] = useState<Working>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    if (!projectId) return;
    const path = row.firestorePath(projectId);
    const segments = path.split('/');
    const ref = doc(db, segments[0], ...segments.slice(1));
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (snap.exists()) {
          setExists(true);
          setStatusDoc(snap.data() as DocStatusDoc);
        } else {
          setExists(false);
          setStatusDoc(null);
        }
      },
      (err) => {
        console.error(`[${row.id}] status subscription error:`, err);
      },
    );
    return () => unsub();
  }, [projectId, row]);

  /* --- Prerequisite (optional; only rows that declare one subscribe) ------
   * `prereqLoaded` starts false so the row never renders an enabled Generate
   * button and then retracts it a moment later — a button that appears and
   * then disables reads as a glitch, and worse, can be clicked in between.
   */
  const prerequisite = row.prerequisite;
  const [prereqState, setPrereqState] = useState<PrerequisiteState | null>(null);
  const [prereqLoaded, setPrereqLoaded] = useState(false);

  useEffect(() => {
    if (!projectId || !prerequisite) return;
    const segments = prerequisite.path(projectId).split('/');
    const ref = doc(db, segments[0], ...segments.slice(1));
    const unsub = onSnapshot(
      ref,
      (snap) => {
        setPrereqState(
          prerequisite.evaluate(snap.exists() ? snap.data() : null, snap.exists(), Date.now()),
        );
        setPrereqLoaded(true);
      },
      (err) => {
        // Fail OPEN: if we cannot read the upstream document we must not lock
        // the user out of a generation they may well be entitled to run. The
        // backend still rejects a genuinely unmet prerequisite, with the same
        // wording, so the worst case is the pre-fix behaviour for this row.
        console.error(`[${row.id}] prerequisite subscription error:`, err);
        setPrereqState(null);
        setPrereqLoaded(true);
      },
    );
    return () => unsub();
  }, [projectId, prerequisite, row.id]);

  /** Unmet only when we have actually read the document and it says so. */
  const prereqUnmet = !!prerequisite && prereqLoaded && prereqState?.met === false;
  const prereqNote: PrerequisiteNote | null = prereqUnmet ? prereqState?.note ?? null : null;
  /** Before the first read, hold the buttons rather than guess. */
  const prereqPending = !!prerequisite && !prereqLoaded;

  const generatedAt = tsToDate(statusDoc?.generatedAt);
  const startedAt = tsToDate(statusDoc?._generationStartedAt);
  const isGeneratingMarker = (() => {
    if (!statusDoc?._generating) return false;
    if (!startedAt) return true;
    return Date.now() - startedAt.getTime() < STALE_GENERATION_MS;
  })();
  const isLocallyGenerating: boolean = working === 'generate';
  const isLocallyDownloading: boolean = working === 'download';
  const showGenerating = isLocallyGenerating || isGeneratingMarker;
  // A stale failure from an earlier attempt must not be shown while the real
  // situation is "the upstream document isn't ready" — otherwise an unmet
  // prerequisite reads as a breakage. The note explains the actual state.
  const hasError = !!statusDoc?._generationError && !showGenerating && !prereqUnmet;
  const isGenerated = exists && !!generatedAt && !showGenerating && !hasError;
  const notYet = !exists && !showGenerating && !hasError;

  const handleGenerate = async () => {
    setActionError(null);
    setWorking('generate');
    try {
      const fn = httpsCallable(functions, row.generateCallable, {
        timeout: CALLABLE_TIMEOUTS.LONG,
      });
      await fn({ projectId });
    } catch (err: any) {
      console.error(`[${row.id}] generation error:`, err);
      setActionError(err?.message || 'Generation failed');
    } finally {
      setWorking(null);
    }
  };

  const handleDownload = async () => {
    setActionError(null);
    setWorking('download');
    try {
      const fn = httpsCallable(functions, row.exportCallable);
      const result = await fn({ projectId });
      const data = result.data as { base64?: string; docx?: string; filename: string };
      const b64 = data.base64 || data.docx;
      if (!b64) throw new Error('Export response did not include document data');
      const byteChars = atob(b64);
      const byteNumbers = new Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) byteNumbers[i] = byteChars.charCodeAt(i);
      const blob = new Blob([new Uint8Array(byteNumbers)], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });
      saveAs(blob, data.filename || `${row.id}.docx`);
    } catch (err: any) {
      console.error(`[${row.id}] export error:`, err);
      setActionError(err?.message || 'Download failed');
    } finally {
      setWorking(null);
    }
  };

  const buttonsDisabled = showGenerating || working !== null || prereqUnmet || prereqPending;

  let badge: React.ReactNode;
  // Built from an earlier data dictionary than the adopted one (design 2026-09-14 §5).
  let dictNotice: DictionaryChangedNotice | null = null;
  if (showGenerating) {
    badge = (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-blue-50 text-blue-700 border border-blue-200">
        <Loader2 className="h-3 w-3 animate-spin" />
        Generating&hellip;
      </span>
    );
  } else if (hasError) {
    badge = (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-red-50 text-red-700 border border-red-200"
        title={statusDoc?._generationError ? displayGenerationError(statusDoc._generationError) : 'Generation failed'}
      >
        <AlertCircle className="h-3 w-3" />
        Generation failed
      </span>
    );
  } else if (isGenerated && generatedAt) {
    dictNotice = dictionaryChangedNotice(adoptedAt, statusDoc, { renameNoticeOpen });
    badge = (
      <span
        className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium bg-green-50 text-green-700 border border-green-200"
        title={generatedAt.toLocaleString()}
      >
        <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
        Generated &middot; {formatRelativeTime(generatedAt)}
      </span>
    );
  } else {
    badge = (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-100 text-gray-600 border border-gray-200">
        Not yet generated
      </span>
    );
  }

  // Each row's action area is laid out as [primary button][regen icon]
  // with a fixed-width regen slot so the primary button column stays
  // aligned across rows regardless of whether a regenerate icon is
  // actually present. `regenSlot` is either the real Regenerate button
  // or an invisible placeholder of the same width.
  let primaryButton: React.ReactNode = null;
  let regenSlot: React.ReactNode = (
    <span key="regen-spacer" className="inline-block h-8 w-8" aria-hidden="true" />
  );

  if (notYet || hasError) {
    if (!canGenerate) {
      primaryButton = (
        <span
          key="gen"
          className="inline-flex items-center h-8 px-3 rounded-md bg-slate-50 text-slate-600 text-xs font-medium ring-1 ring-inset ring-slate-200"
        >
          Not yet generated
        </span>
      );
    } else {
      primaryButton = (
        <button
          key="gen"
          type="button"
          onClick={handleGenerate}
          disabled={buttonsDisabled}
          title="Generate this document"
          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-primary-600 hover:bg-primary-700 text-white text-xs font-semibold shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isLocallyGenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4 fill-white" />}
          <span>Generate</span>
        </button>
      );
    }
  } else if (isGenerated) {
    primaryButton = (
      <button
        key="dl"
        type="button"
        onClick={handleDownload}
        disabled={buttonsDisabled}
        title="Download DOCX"
        className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {isLocallyDownloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
        <span>Download</span>
      </button>
    );
    regenSlot = (
      <button
        key="regen"
        type="button"
        onClick={handleGenerate}
        disabled={buttonsDisabled || !canGenerate}
        title={canGenerate ? 'Regenerate' : "Your role doesn't allow regenerating documents."}
        className="inline-flex items-center justify-center h-8 w-8 rounded-md text-gray-500 hover:text-gray-800 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        {isLocallyGenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
      </button>
    );
  } else if (showGenerating) {
    primaryButton = (
      <span key="spinner" className="inline-flex items-center justify-center h-8 w-8 text-gray-400">
        <Loader2 className="h-4 w-4 animate-spin" />
      </span>
    );
  }

  // With the prerequisite unmet the buttons are replaced by the note, so there
  // is nothing to click and nothing to explain away.
  const actionButtons: React.ReactNode[] = prereqNote ? [] : [primaryButton, regenSlot];

  return (
    <RowFrame
      icon={row.icon}
      title={row.title}
      shortName={row.shortName}
      description={row.description}
      badge={badge}
      actions={actionButtons}
      actionError={actionError}
      dictionaryNotice={dictNotice?.text ?? null}
      renameNotice={isGenerated ? (
        <RenameNotice
          compact
          projectId={projectId}
          documentPath={row.firestorePath(projectId)}
          onRegenerate={handleGenerate}
          regenerating={working === 'generate'}
        />
      ) : null}
      note={prereqNote}
    />
  );
}

/* ---------- Template row (static download, no Firestore status) ---------- */

interface TemplateRowProps {
  row: TemplateRow;
  projectId: string;
}

function TemplateRowView({ row, projectId }: TemplateRowProps) {
  const [downloading, setDownloading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const handleDownload = async () => {
    setActionError(null);
    setDownloading(true);
    try {
      const fn = httpsCallable(functions, row.exportCallable);
      const result = await fn({ projectId });
      const data = result.data as { base64?: string; docx?: string; filename: string };
      const b64 = data.base64 || data.docx;
      if (!b64) throw new Error('Export response did not include document data');
      const byteChars = atob(b64);
      const byteNumbers = new Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) byteNumbers[i] = byteChars.charCodeAt(i);
      const blob = new Blob([new Uint8Array(byteNumbers)], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });
      saveAs(blob, data.filename || `${row.id}.docx`);
    } catch (err: any) {
      console.error(`[${row.id}] template download error:`, err);
      setActionError(err?.message || 'Download failed');
    } finally {
      setDownloading(false);
    }
  };

  const badge = (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200"
      title="Static template — always available"
    >
      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
      Available
    </span>
  );

  // Match the GenerateRowView layout — [primary button][regen spacer]
  // — so the Download button column lines up across all rows.
  const actions = [
    <button
      key="dl"
      type="button"
      onClick={handleDownload}
      disabled={downloading}
      title="Download blank template DOCX"
      className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
    >
      {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
      <span>Download</span>
    </button>,
    <span key="regen-spacer" className="inline-block h-8 w-8" aria-hidden="true" />,
  ];

  return (
    <RowFrame
      icon={row.icon}
      title={row.title}
      shortName={row.shortName}
      description={row.description}
      badge={badge}
      actions={actions}
      actionError={actionError}
    />
  );
}

/* ---------- Mock Data row (modal collects record count, downloads ZIP) ---------- */

interface MockDataRowProps {
  row: MockDataRow;
  projectId: string;
}

interface PreviewFormPayload {
  filename: string;
  formCode: string;
  headers: string[];
  rows: string[][];
  totalRowCount: number;
}

interface PreviewPayload {
  preview: true;
  manifest: {
    edcSystem: string;
    edcSupported: boolean;
    subjectCount: number;
    formCount: number;
    visitCount: number;
    violationCount?: number;
    notes: string[];
  };
  forms: PreviewFormPayload[];
  negativeTestManifestPreview?: {
    headers: string[];
    rows: string[][];
    totalRowCount: number;
  };
}

function MockDataRowView({ row, projectId }: MockDataRowProps) {
  const canGenerate = usePermission('generate');
  const [open, setOpen] = useState(false);
  const [recordCount, setRecordCount] = useState<number>(10);
  const [recordIdField, setRecordIdField] = useState<string>('record_id');
  const [negativeTestingEnabled, setNegativeTestingEnabled] = useState<boolean>(false);
  const [negativeTestingPercent, setNegativeTestingPercent] = useState<number>(10);
  const [working, setWorking] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [lastNotice, setLastNotice] = useState<string | null>(null);
  const [preview, setPreview] = useState<PreviewPayload | null>(null);
  const [activeFormIdx, setActiveFormIdx] = useState<number>(0);

  const callableArgs = () => ({
    projectId,
    recordCount,
    recordIdField,
    negativeTestingPercent: negativeTestingEnabled ? negativeTestingPercent : 0,
  });

  const handlePreview = async () => {
    setActionError(null);
    setPreview(null);
    setPreviewing(true);
    try {
      const fn = httpsCallable(functions, row.callable, {
        timeout: CALLABLE_TIMEOUTS.LONG,
      });
      const result = await fn({ ...callableArgs(), preview: true });
      const data = result.data as PreviewPayload;
      setPreview(data);
      setActiveFormIdx(0);
    } catch (err: any) {
      console.error(`[${row.id}] preview error:`, err);
      setActionError(err?.message || 'Preview failed');
    } finally {
      setPreviewing(false);
    }
  };

  const handleGenerate = async () => {
    setActionError(null);
    setWorking(true);
    try {
      const fn = httpsCallable(functions, row.callable, {
        timeout: CALLABLE_TIMEOUTS.LONG,
      });
      const result = await fn(callableArgs());
      const data = result.data as {
        downloadUrl: string;
        filename: string;
        mimeType: string;
        manifest?: {
          edcSystem: string;
          edcSupported: boolean;
          subjectCount: number;
          formCount: number;
          violationCount?: number;
          notes: string[];
        };
      };
      const resp = await fetch(data.downloadUrl);
      if (!resp.ok) throw new Error(`Download failed: ${resp.status}`);
      const blob = await resp.blob();
      saveAs(new Blob([blob], { type: data.mimeType }), data.filename || 'mock-data.zip');

      const m = data.manifest;
      if (m) {
        const lines: string[] = [];
        lines.push(`Generated ${m.subjectCount} subjects across ${m.formCount} forms (EDC: ${m.edcSystem})`);
        if (!m.edcSupported) {
          lines.push('Native bulk-import format not yet supported for this EDC — see README inside the ZIP.');
        }
        if (typeof m.violationCount === 'number' && m.violationCount > 0) {
          lines.push(`Seeded ${m.violationCount} negative-test violations (see negative_test_manifest.csv).`);
        }
        if (m.notes?.length) lines.push(...m.notes);
        setLastNotice(lines.join(' '));
      }
      setOpen(false);
    } catch (err: any) {
      console.error(`[${row.id}] mock data generation error:`, err);
      setActionError(err?.message || 'Generation failed');
    } finally {
      setWorking(false);
    }
  };

  const badge = (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200"
      title="Generates on demand — does not persist between runs"
    >
      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
      Available
    </span>
  );

  const actions = canGenerate
    ? [
        <button
          key="open"
          type="button"
          onClick={() => {
            setActionError(null);
            setOpen(true);
          }}
          disabled={working}
          title="Configure and generate mock data"
          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {working ? <Loader2 className="h-4 w-4 animate-spin" /> : <Database className="h-4 w-4" />}
          <span>Generate</span>
        </button>,
        <span key="regen-spacer" className="inline-block h-8 w-8" aria-hidden="true" />,
      ]
    : [
        <span
          key="open"
          className="inline-flex items-center h-8 px-3 rounded-md bg-slate-50 text-slate-600 text-xs font-medium ring-1 ring-inset ring-slate-200"
        >
          Generate-on-demand &mdash; restricted to Editors
        </span>,
        <span key="regen-spacer" className="inline-block h-8 w-8" aria-hidden="true" />,
      ];

  return (
    <>
      <RowFrame
        icon={row.icon}
        title={row.title}
        shortName={row.shortName}
        description={row.description}
        badge={badge}
        actions={actions}
        actionError={actionError || lastNotice}
      />
      {open && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => !working && !previewing && setOpen(false)}>
          <div
            className={`bg-white rounded-lg shadow-xl w-full p-5 max-h-[90vh] overflow-y-auto ${preview ? 'max-w-4xl' : 'max-w-md'}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Generate Mock Data</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Synthetic records based on your CRF forms, visit schedule, and CT codelist bindings.
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  if (working || previewing) return;
                  setOpen(false);
                  setPreview(null);
                }}
                className="text-gray-400 hover:text-gray-600"
                disabled={working || previewing}
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <label className="block text-xs font-medium text-gray-700 mb-1">
              Number of subjects
            </label>
            <input
              type="number"
              min={1}
              max={500}
              value={recordCount}
              onChange={(e) => setRecordCount(Math.max(1, Math.min(500, parseInt(e.target.value || '0', 10) || 0)))}
              disabled={working}
              className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-emerald-500"
            />
            <p className="text-[11px] text-gray-500 mt-1">Range: 1–500. Each subject gets one row per (form × visit) where the form is collected.</p>

            <label className="block text-xs font-medium text-gray-700 mt-3 mb-1">
              REDCap record-ID field name
            </label>
            <input
              type="text"
              value={recordIdField}
              onChange={(e) => setRecordIdField(e.target.value)}
              disabled={working}
              placeholder="record_id"
              className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-emerald-500 font-mono"
            />
            <p className="text-[11px] text-gray-500 mt-1">Default <code className="font-mono">record_id</code>. Override if your REDCap project renamed the primary key (e.g., <code className="font-mono">study_id</code>, <code className="font-mono">subject_id</code>).</p>

            <div className="mt-4 pt-3 border-t border-gray-200">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={negativeTestingEnabled}
                  onChange={(e) => setNegativeTestingEnabled(e.target.checked)}
                  disabled={working}
                  className="mt-0.5 h-3.5 w-3.5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                />
                <span className="text-xs text-gray-700">
                  <span className="font-medium">Include negative-test records</span>
                  <span className="block text-[11px] text-gray-500 mt-0.5">Seed a percentage of rows with deliberate edit-check violations (out-of-range, blank-required, max-length-exceeded, invalid picklist) and emit a manifest mapping each seeded row to the rule it should trigger.</span>
                </span>
              </label>
              {negativeTestingEnabled && (
                <div className="mt-2 ml-5">
                  <label className="block text-[11px] font-medium text-gray-700 mb-1">
                    Violation rate: <span className="font-mono">{negativeTestingPercent}%</span>
                  </label>
                  <input
                    type="range"
                    min={1}
                    max={50}
                    value={negativeTestingPercent}
                    onChange={(e) => setNegativeTestingPercent(parseInt(e.target.value, 10))}
                    disabled={working}
                    className="w-full accent-emerald-600"
                  />
                  <p className="text-[11px] text-gray-500">Range 1–50%. Each row independently rolls against this probability; one violation per affected row.</p>
                </div>
              )}
            </div>

            {actionError && (
              <p className="text-xs text-red-600 mt-3" role="alert">{actionError}</p>
            )}

            {preview && (
              <PreviewTable
                preview={preview}
                activeFormIdx={activeFormIdx}
                setActiveFormIdx={setActiveFormIdx}
              />
            )}

            <div className="flex justify-end gap-2 mt-5">
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  setPreview(null);
                }}
                disabled={working || previewing}
                className="px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100 rounded-md disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handlePreview}
                disabled={working || previewing || recordCount < 1}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-emerald-700 bg-white border border-emerald-600 hover:bg-emerald-50 rounded-md disabled:opacity-50"
              >
                {previewing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />}
                <span>{previewing ? 'Previewing…' : preview ? 'Re-preview' : 'Preview'}</span>
              </button>
              <button
                type="button"
                onClick={handleGenerate}
                disabled={working || previewing || recordCount < 1}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-md disabled:opacity-50"
              >
                {working ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                <span>{working ? 'Generating…' : 'Generate & download ZIP'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* ---------- Mock Data preview table ---------- */

interface PreviewTableProps {
  preview: PreviewPayload;
  activeFormIdx: number;
  setActiveFormIdx: (n: number) => void;
}

function PreviewTable({ preview, activeFormIdx, setActiveFormIdx }: PreviewTableProps) {
  const safeIdx = Math.min(activeFormIdx, Math.max(0, preview.forms.length - 1));
  const form = preview.forms[safeIdx];

  return (
    <div className="mt-4 pt-3 border-t border-gray-200">
      <div className="flex items-center justify-between mb-2 gap-2">
        <h4 className="text-xs font-semibold text-gray-900">
          Preview — {preview.manifest.subjectCount} sample subject{preview.manifest.subjectCount === 1 ? '' : 's'}
        </h4>
        {typeof preview.manifest.violationCount === 'number' && preview.manifest.violationCount > 0 && (
          <span className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">
            {preview.manifest.violationCount} negative-test row{preview.manifest.violationCount === 1 ? '' : 's'} seeded
          </span>
        )}
      </div>

      {preview.forms.length === 0 && (
        <p className="text-xs text-gray-500 italic">No forms produced any rows for this configuration.</p>
      )}

      {preview.forms.length > 0 && (
        <>
          <div className="flex items-center gap-2 mb-2">
            <label className="text-[11px] text-gray-600 font-medium">Form:</label>
            <select
              value={safeIdx}
              onChange={(e) => setActiveFormIdx(parseInt(e.target.value, 10))}
              className="text-xs border border-gray-300 rounded px-2 py-1 max-w-[260px]"
            >
              {preview.forms.map((f, i) => (
                <option key={f.formCode} value={i}>
                  {f.filename} ({f.totalRowCount} row{f.totalRowCount === 1 ? '' : 's'})
                </option>
              ))}
            </select>
            <span className="text-[11px] text-gray-500">
              showing {form.rows.length} of {form.totalRowCount}
            </span>
          </div>

          <div className="border border-gray-200 rounded overflow-x-auto max-h-[320px]">
            <table className="text-[11px] font-mono w-full">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  {form.headers.map((h, i) => (
                    <th
                      key={i}
                      className="px-2 py-1 text-left font-semibold text-gray-700 border-b border-gray-200 whitespace-nowrap"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {form.rows.map((row, ri) => (
                  <tr key={ri} className={ri % 2 === 0 ? 'bg-white' : 'bg-gray-50/40'}>
                    {row.map((cell, ci) => (
                      <td
                        key={ci}
                        className="px-2 py-1 text-gray-800 border-b border-gray-100 whitespace-nowrap"
                        title={cell}
                      >
                        {cell.length > 40 ? cell.slice(0, 40) + '…' : cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {preview.negativeTestManifestPreview && preview.negativeTestManifestPreview.totalRowCount > 0 && (
        <div className="mt-3">
          <h4 className="text-xs font-semibold text-gray-900 mb-1">
            Negative-test manifest preview ({preview.negativeTestManifestPreview.totalRowCount} total)
          </h4>
          <div className="border border-gray-200 rounded overflow-x-auto max-h-[200px]">
            <table className="text-[11px] font-mono w-full">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  {preview.negativeTestManifestPreview.headers.map((h, i) => (
                    <th key={i} className="px-2 py-1 text-left font-semibold text-gray-700 border-b border-gray-200 whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.negativeTestManifestPreview.rows.map((row, ri) => (
                  <tr key={ri} className={ri % 2 === 0 ? 'bg-white' : 'bg-gray-50/40'}>
                    {row.map((cell, ci) => (
                      <td key={ci} className="px-2 py-1 text-gray-800 border-b border-gray-100 whitespace-nowrap" title={cell}>
                        {cell.length > 60 ? cell.slice(0, 60) + '…' : cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {preview.manifest.notes.length > 0 && (
        <ul className="mt-3 space-y-0.5">
          {preview.manifest.notes.map((n, i) => (
            <li key={i} className="text-[11px] text-gray-500">• {n}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ---------- Coming soon row ---------- */

function ComingSoonRowView({ row }: { row: ComingSoonRow }) {
  const badge = (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-100 text-gray-500 border border-gray-200">
      Coming Soon
    </span>
  );
  return (
    <RowFrame
      icon={row.icon}
      title={row.title}
      shortName={row.shortName}
      description={row.description}
      badge={badge}
      actions={[]}
      actionError={null}
      muted
    />
  );
}

/* ---------- Shared row frame (matches RequirementsDesignCard rows) ---------- */

interface RowFrameProps {
  icon: React.ReactNode;
  title: string;
  shortName: string;
  description: string;
  badge: React.ReactNode;
  actions: React.ReactNode[];
  actionError: string | null;
  /** "Regenerate it" sentence for a document built from an earlier data dictionary. */
  dictionaryNotice?: string | null;
  /** The rename notice (features/renames), for a generated row. */
  renameNotice?: React.ReactNode;
  muted?: boolean;
  /**
   * Unmet-prerequisite note. Rendered full width beneath the row, in place of
   * the action buttons — the text is a sentence, and the action column is a
   * narrow fixed slot that would truncate it to uselessness.
   */
  note?: PrerequisiteNote | null;
}

function RowFrame({ icon, title, shortName, description, badge, actions, actionError, dictionaryNotice, renameNotice, muted, note }: RowFrameProps) {
  return (
    <div className={`rounded-lg border-l-4 ${TESTING_THEME.rowAccent} ${muted ? 'opacity-70' : 'hover:bg-gray-50'} transition-colors`}>
    <div
      className="flex items-center gap-3 px-3 py-2.5"
    >
      <div
        className={`h-7 w-7 rounded-md flex items-center justify-center ${TESTING_THEME.iconBg} ${TESTING_THEME.iconText} flex-shrink-0`}
      >
        {icon || <FileText className="h-4 w-4" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-gray-900 truncate flex items-center gap-2">
          <span className="truncate">{title}</span>
          <span className="text-[10px] font-mono text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded flex-shrink-0">
            {shortName}
          </span>
        </div>
        <p className="text-xs text-gray-500 truncate" title={description}>
          {description}
        </p>
        {actionError && (
          <p className="text-[11px] text-red-600 mt-0.5 truncate" title={actionError}>
            {actionError}
          </p>
        )}
        {dictionaryNotice && (
          <p className="text-[11px] text-amber-700 mt-0.5">{dictionaryNotice}</p>
        )}
        {renameNotice}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {badge}
        {actions.length > 0 && <div className="flex items-center gap-1">{actions}</div>}
      </div>
    </div>
      {note && <PrerequisiteNoteBlock note={note} />}
    </div>
  );
}

/**
 * A prerequisite is a normal, expected state — not a failure — so this is
 * deliberately grey (or blue while something is running) and never red.
 * `progress` earns a spinner because the user's only action is to wait.
 */
function PrerequisiteNoteBlock({ note }: { note: PrerequisiteNote }) {
  const isProgress = note.tone === 'progress';
  return (
    <div className="px-3 pb-2.5 -mt-0.5">
      <div
        className={`text-xs rounded-lg p-2 flex items-start gap-2 border ${
          isProgress
            ? 'text-blue-700 bg-blue-50 border-blue-200'
            : 'text-gray-600 bg-gray-50 border-gray-200'
        }`}
      >
        {isProgress ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin mt-px shrink-0" />
        ) : (
          <Info className="h-3.5 w-3.5 mt-px shrink-0 text-gray-400" />
        )}
        <span>{note.text}</span>
      </div>
    </div>
  );
}

/* ---------- Card ---------- */

interface TestingQualificationCardProps {
  projectId: string;
}

export default function TestingQualificationCard({ projectId }: TestingQualificationCardProps) {
  const [expanded, setExpanded] = useState(true);

  // One read of the adoption record for every row: a row built from an earlier
  // dictionary than the adopted one says to regenerate it (design 2026-09-14 §5).
  const [adoption, setAdoption] = useState<{ adoptedAt?: unknown } | null>(null);
  useEffect(() => {
    if (!projectId) return;
    const unsub = onSnapshot(
      dataManagementDoc(projectId, 'dictionaryAdoption'),
      (snap) => {
        setAdoption(snap.exists() ? snap.data() : null);
      },
      (err) => {
        console.error('[testing] dictionary adoption subscription error:', err);
      },
    );
    return () => unsub();
  }, [projectId]);

  const availableCount = ROWS.filter((r) => r.mode !== 'comingSoon').length;

  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border ${TESTING_THEME.headerBg} hover:opacity-90 transition-colors`}
      >
        {expanded ? (
          <ChevronDown className={`h-4 w-4 ${TESTING_THEME.headerText}`} />
        ) : (
          <ChevronRight className={`h-4 w-4 ${TESTING_THEME.headerText}`} />
        )}
        <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${TESTING_THEME.iconBg} ${TESTING_THEME.iconText}`}>
          <TestTube2 className="h-4 w-4" />
        </div>
        <div className="flex-1 text-left">
          <div className={`text-sm font-semibold ${TESTING_THEME.headerText}`}>Testing & Qualification</div>
          <p className="text-xs text-gray-600 mt-0.5">
            Test plans, procedures, and qualification protocols for the study build.
          </p>
        </div>
        <span className={`text-xs font-semibold ${TESTING_THEME.headerText} px-2 py-0.5 rounded-full bg-white/60 mr-2`}>
          {ROWS.length} documents
        </span>
        <span className={`text-xs ${TESTING_THEME.headerText} opacity-80`}>{availableCount} available</span>
      </button>

      {expanded && (
        <div className="space-y-1 pl-2">
          {ROWS.map((row) => {
            if (row.mode === 'generate') return <GenerateRowView key={row.id} row={row} projectId={projectId} adoptedAt={adoption?.adoptedAt} />;
            if (row.mode === 'template') return <TemplateRowView key={row.id} row={row} projectId={projectId} />;
            if (row.mode === 'mockData') return <MockDataRowView key={row.id} row={row} projectId={projectId} />;
            return <ComingSoonRowView key={row.id} row={row} />;
          })}
        </div>
      )}
    </div>
  );
}
